import {
	CameraFilled,
	CheckCircleFilled,
	PictureFilled,
} from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Image } from "components/Image/Image"
import { useNotification } from "components/Notification/Notification"
import { Upload, type UploadProps } from "components/Upload/Upload"
import { Visit } from "interface/visit"
import { useUpdateVisitPhotoFile } from "services/visits"
import { CONSTRAINTS } from "utils/file"
import { useStepCompletion } from "./useStepCompletion"

export function StepUploadImage({ visit }: { visit: Visit }) {
	const { notify } = useNotification()
	const { markStepAs, imageUpload } = useStepCompletion(visit)
	const {
		mutate: updateVisitPhotoFile,
		isError,
		isPending,
	} = useUpdateVisitPhotoFile(visit.id)
	const isAlreadyUploaded = !!visit.installationPhotoUrl
	const takeAPhotoLabel = isAlreadyUploaded
		? "Take a new photo"
		: "Take a photo"
	const handleChange: UploadProps["onChange"] = (param) => {
		const file = param.file as unknown as File

		if (!CONSTRAINTS.ALLOWED_MIME_TYPES.includes(file.type)) {
			notify.error({
				message: `The supported file types are ${CONSTRAINTS.ALLOWED_MIME_TYPES_WKN.join(", ")}`,
			})
		} else if (file.size > CONSTRAINTS.MAX_FILE_SIZE) {
			notify.error({ message: CONSTRAINTS.MAX_SIZE_ERROR_MESSAGE })
		} else {
			updateVisitPhotoFile(file)
		}

		return
	}

	return (
		<div className="flex size-full grow flex-col items-center justify-center gap-3 text-center">
			{imageUpload && isAlreadyUploaded && (
				<>
					<CheckCircleFilled
						aria-hidden
						className="text-[48px] text-green-60"
					/>
					<span>The image is uploaded</span>
				</>
			)}

			{!imageUpload && (
				<>
					Upload image documenting visit
					<div className="relative flex flex-col items-center gap-1">
						{isAlreadyUploaded && (
							<Image
								preview
								alt="Uploaded image"
								src={visit.installationPhotoUrl}
								rootClassName="rounded-2xl"
								className="size-20 rounded-2xl bg-white object-cover"
								classNames={{ cover: "rounded-2xl" }}
							/>
						)}

						{!isAlreadyUploaded && (
							<Upload.Dragger
								className="[&_.ant-upload]:p-0 [&_.ant-upload-drag]:rounded-2xl [&_.ant-upload-drag]:border-0"
								data-testid={`Select a photo with ${visit.patient?.fullName}'s visit`}
								maxCount={1}
								accept="image/*"
								showUploadList={false}
								beforeUpload={(file) => {
									if (!file.type.includes("image/")) {
										return Upload.LIST_IGNORE
									}
									return false
								}}
								onChange={handleChange}
							>
								<div className="size-20 place-content-center">
									<PictureFilled aria-hidden className="text-2xl" />
								</div>
							</Upload.Dragger>
						)}

						<div className="flex max-w-lg flex-col gap-2">
							<Upload
								data-testid={`${takeAPhotoLabel} of ${visit.patient?.fullName}'s visit`}
								maxCount={1}
								capture="environment"
								accept={CONSTRAINTS.ALLOWED_MIME_TYPES.join(",")}
								showUploadList={false}
								beforeUpload={() => false}
								onChange={handleChange}
							>
								<Button
									type="link"
									className="w-full"
									icon={<CameraFilled aria-hidden />}
									loading={isPending}
								>
									{takeAPhotoLabel}
								</Button>
							</Upload>

							<ErrorStatus show={isError && !isPending}>
								Failed to upload image. Please, try again.
							</ErrorStatus>
						</div>
					</div>
					<Button
						type="primary"
						disabled={!isAlreadyUploaded || isError}
						className="mt-auto w-full"
						onClick={() => markStepAs("imageUpload")}
					>
						Mark as complete
					</Button>
				</>
			)}
		</div>
	)
}
